/* DS V2 search batch: the vocabulary / not-stocked rules / copy live in fk-search-synonyms.js (window.FKSearch). */
function fkNorm(raw) {
  if (window.FKSearch) return window.FKSearch.normalise(raw);
  var q = String(raw || '').trim();
  return { original: q, query: q.toLowerCase(), mapped: false, notStocked: null };
}

class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.currentKey = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    // Tapping the box again after Escape / a closed dropdown re-opens it
    this.input.addEventListener('click', () => {
      if (!this.isOpen) this.onFocus();
    });
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      var _wrapper = this.querySelector('#predictive-search-results-groups-wrapper'); _wrapper && _wrapper.remove();
    }

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      // Empty box: show the popular searches + categories instead of closing
      if (this.contains(document.activeElement)) {
        this.showHomeState();
      } else {
        this.close(true);
      }
      return;
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(fkNorm(newSearchTerm).query);
    // Never leave the popular-searches panel (or nothing) under a typed term while results load
    if (this.homeShown || !this.querySelector('[data-fk-search-term]')) this.renderPendingRow(fkNorm(newSearchTerm).query);

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
      // Cleared box with focus: offer the popular searches again
      this.showHomeState();
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) {
      this.showHomeState();
      return;
    }

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true' && !this.homeShown) {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length && event.code !== 'Escape' && event.code !== 'Tab' && !this.homeShown && this.contains(document.activeElement)) {
      this.showHomeState();
    }
    event.preventDefault();

    switch (event.key || event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
    }
  }

  onKeydown(event) {
    // Enter on a keyboard-selected row follows that row's link (product, collection, suggestion, chip, page).
    // Handled on keydown so the browser's own form submit never races it; with no row selected, or on the
    // "Search for" row, the form submits as usual (the formdata hook in fk-search-synonyms.js normalises the term).
    if ((event.key === 'Enter' || event.code === 'Enter' || event.code === 'NumpadEnter') && !event.isComposing) {
      const link = this.querySelector('[aria-selected="true"] a[href]');
      if (link) {
        event.preventDefault();
        link.click();
        return;
      }
    }
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
    // Escape closes the dropdown and keeps focus in the box
    if (event.code === 'Escape' && this.isOpen) {
      event.preventDefault(); // type=search boxes would otherwise also clear their text
      this.close();
      this.input.focus();
    }
  }

  updateSearchForTerm(term) {
    const el = this.querySelector('[data-fk-search-term]');
    if (el) el.textContent = term;
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    if (this.statusElement) this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const norm = fkNorm(searchTerm);
    const queryKey = norm.query.replace(/\s+/g, '-');
    this.currentKey = queryKey;
    this.homeShown = false;
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey], norm);
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(norm.query)}&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const section = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search');
        const resultsMarkup = section ? section.innerHTML : '';
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        // Ignore answers to a term the person has already typed past
        if (queryKey !== this.currentKey) return;
        this.renderSearchResults(resultsMarkup, norm);
      })
      .catch((error) => {
        if (error && error.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');
    if (!this.statusElement) return;

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    if (!this.statusElement) return;
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup, norm) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);
    this.homeShown = false;
    norm = norm || fkNorm(this.searchTerm);
    this.decorateResults(norm);

    this.setLiveRegionResults(norm);
    this.open();
  }

  /* Adds, on top of the server markup: the mapped-term line, the "not stocked" note,
     "Other products" labelling and the zero-results help (never a blank dropdown). */
  decorateResults(norm) {
    const ui = window.FKSearch && window.FKSearch.ui;
    const listbox = this.predictiveSearchResults.querySelector('#predictive-search-results');
    const meta = this.predictiveSearchResults.querySelector('[data-fk-search-meta]');
    const total = meta ? parseInt(meta.getAttribute('data-total'), 10) || 0 : 0;
    this.updateSearchForTerm(norm.query);
    if (!ui || !listbox) return;

    if (norm.notStocked) {
      listbox.insertAdjacentHTML('beforebegin', ui.notStockedHtml(norm.notStocked));
      const heading = this.predictiveSearchResults.querySelector('#predictive-search-products');
      if (heading) heading.textContent = window.FKSearch.STRINGS.otherProducts;
    } else if (norm.mapped) {
      listbox.insertAdjacentHTML('beforebegin', ui.mappedHtml(norm));
    }

    if (total === 0) {
      /* Zero results: the "Search for" row would contradict "No results for", so it goes */
      const searchFor = this.predictiveSearchResults.querySelector('#predictive-search-option-search-keywords');
      if (searchFor) searchFor.remove();
      const term = norm.original;
      const holder = document.createElement('div');
      holder.setAttribute('data-fk-search-zero-holder', '');
      listbox.appendChild(holder);
      const fill = (chips) => {
        if (!holder.isConnected) return;
        holder.innerHTML = ui.zeroHtml(term, norm, chips, { option: true });
      };
      fill([]);
      window.FKSearch.popularChips().then(fill);
    }
  }

  /* Instant "Search for “term”" row while the first results are on their way. */
  renderPendingRow(term) {
    const cfg = window.FKSearch && window.FKSearch.config();
    const ui = window.FKSearch && window.FKSearch.ui;
    if (!cfg || !cfg.searchFor || !ui) return;
    const label = ui.esc(cfg.searchFor).replace('@@', '<span data-fk-search-term>' + ui.esc(term) + '</span>');
    this.predictiveSearchResults.innerHTML =
      '<div id="predictive-search-results" role="listbox"><div id="predictive-search-option-search-keywords" class="predictive-search__search-for-button">' +
      '<button type="submit" class="predictive-search__item predictive-search__item--term link link--text h5 animate-arrow" tabindex="-1" role="option" aria-selected="false">' +
      '<span data-predictive-search-search-for-text>' + label + '</span>' + (cfg.arrowIcon || '') + '</button></div></div>';
    this.homeShown = false;
    this.removeAttribute('results');
    this.open();
  }

  /* Empty box: popular searches (only those that return results) + non-empty categories. */
  showHomeState() {
    const ui = window.FKSearch && window.FKSearch.ui;
    if (!ui) return;
    this.currentKey = '';
    this.homeShown = true;
    this.removeAttribute('loading');
    this.removeAttribute('results');
    const build = (chips) => {
      if (!this.homeShown) return;
      const html = ui.chipsHtml(chips, { option: true }) + ui.categoriesHtml({ option: true });
      if (!html) return;
      this.predictiveSearchResults.innerHTML =
        '<div id="predictive-search-results" role="listbox" class="fk-search-home" aria-label="' + ui.esc(window.FKSearch.STRINGS.homeAnnounce) + '">' + html + '</div>';
      this.input.setAttribute('aria-activedescendant', '');
      this.open();
    };
    // Open with the categories straight away; chips join as soon as they are verified
    build([]);
    window.FKSearch.popularChips().then((chips) => {
      if (!this.homeShown) return;
      build(chips);
    });
  }

  setLiveRegionResults(norm) {
    this.removeAttribute('loading');
    const el = this.querySelector('[data-predictive-search-live-region-count-value]');
    let text = el ? el.textContent.trim() : '';
    if (norm && norm.notStocked) text = window.FKSearch.STRINGS.notStocked(norm.notStocked.label) + ' ' + text;
    else if (norm && norm.mapped) text = window.FKSearch.STRINGS.showingFor + ' ' + norm.query + '. ' + text;
    this.setLiveRegionText(text);
  }

  getResultsMaxHeight() {
    const header = document.querySelector('.section-header');
    this.resultsMaxHeight = window.innerHeight - (header ? header.getBoundingClientRect().bottom : 0);
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
